<template>
  <b-dropdown
    size="sm"
    :toggle-class="toggleClassName"
    :id="dropdownId"
    :class="menuClassName"
  >
    <b-dropdown-item v-b-modal="'new-cft-' + app.uuid" v-if="app.isTemplate">
      <b-link>
        <span>{{ $t("newPageFromPageTemplate") }}</span>
      </b-link>
      <new-content-from-template-modal
        :app-id="app.uuid"
        :version="app.version"
        :templates="templates"
      ></new-content-from-template-modal>
    </b-dropdown-item>

    <b-dropdown-item
      :link-class="exportBtnClass"
      @click="downloadTemplate"
      :disabled="shouldDisableExport"
      v-if="app.isTemplate"
    >
      <b-link>
        <span>{{ $t("export") }}</span>
      </b-link>
    </b-dropdown-item>

    <b-dropdown-item
      v-b-modal="'import-template-' + app.uuid"
      v-if="app.isTemplate"
    >
      <b-link>
        <span>{{ $t("newVersionFromFile") }}</span>
      </b-link>
      <import-template-modal
        :is-update-template="true"
        :app-id="app.uuid"
        :template="app"
      ></import-template-modal>
    </b-dropdown-item>

    <b-dropdown-item v-b-modal="'new-ccv-' + this.app.uuid">
      <b-link v-if="isCustomContent">
        <span>{{ $t("newVersion") }}</span>
      </b-link>
    </b-dropdown-item>

    <new-custom-content-version-modal
      v-if="isCustomContent"
      :app="app"
    ></new-custom-content-version-modal>

    <b-dropdown-item @click="goToRoute('DataPalette')" v-if="!isCustomContent">
      <b-link>
        <span>{{ editText }}</span>
      </b-link>
    </b-dropdown-item>

    <b-dropdown-item v-if="!isCustomContent">
      <b-link :href="pagesPath" @click="goToRoute('Preview')">
        <span>{{ previewText }}</span>
      </b-link>
    </b-dropdown-item>

    <b-dropdown-item @click="goToRoute('Deploy')">
      <b-link v-if="shouldShowDeploy">
        <span>{{ deployText }}</span>
      </b-link>
    </b-dropdown-item>

    <b-dropdown-item
      v-if="!app.isTemplate && !isCustomContent"
      @click="convertToTemplateConfirm"
    >
      <b-link>
        <span>{{ $t("convertToPageTemplate") }}</span>
      </b-link>
    </b-dropdown-item>

    <b-dropdown-item
      v-if="isCreatedFromTemplate"
      @click="updateSourceTemplateConfirm"
    >
      <b-link>
        <span>{{ $t("updateSourcePageTemplate") }}</span>
      </b-link>
    </b-dropdown-item>

    <b-dropdown-item v-b-modal="'archive-confirm-' + app.uuid">
      <b-link>
        <span>{{ $t("delete") }}</span>
      </b-link>
    </b-dropdown-item>
  </b-dropdown>
</template>

<script>
  import { mapState } from "vuex";
  import utils from "@/utils";
  import templateServices from "@/services/templates-service";
  import services from "@/services";
  import templateService from "@/services/templates-service";
  import {
    HTML_REPORT_FIELD_NAMES,
    MUTATION_NAME,
    TOAST_CONFIG
  } from "@/static/global-variables";
  import { QUERIES } from "@/static/queries";
  import ImportTemplateModal from "@/components/ImportTemplateModal";
  import NewContentFromTemplateModal from "@/components/NewContentFromTemplateModal";
  import store from "@/store";
  import NewCustomContentVersionModal from "@/components/NewCustomContentVersionModal.vue";

  export default {
    name: "CardActionMenu",
    props: {
      app: Object,
      isMiniMode: Boolean
    },
    components: {
      NewCustomContentVersionModal,
      NewContentFromTemplateModal,
      ImportTemplateModal
    },
    computed: {
      ...mapState({
        apps: "apps",
        messages: "messages",
        objectDescribe: "objectDescribe"
      }),
      templates() {
        return this.apps.filter(a => !!a.isTemplate);
      },
      menuClassName() {
        return this.isMiniMode ? "m-left-auto" : "";
      },
      toggleClassName() {
        return this.isMiniMode ? "l-caret" : "v-dropdown";
      },
      shouldDisableExport() {
        return !this.app.version;
      },
      isCreatedFromTemplate() {
        return !!this.app.sourceTemplateUuid;
      },
      exportBtnClass() {
        return this.shouldDisableExport ? "disable-exp-btn" : "";
      },
      shouldShowDeploy() {
        let fieldFLS = utils.findFieldFLS(
          this.objectDescribe[QUERIES.htmlReport.object],
          HTML_REPORT_FIELD_NAMES.Studio_Id_vod__c
        );
        return fieldFLS.hasRead;
      },
      isCustomContent() {
        return this.app.customContent;
      },
      dropdownId() {
        return this.app.name.split(" ").join("_");
      },
      editText() {
        return this.getMessageText(this.messages.Edit);
      },
      previewText() {
        return this.getMessageText(this.messages.PREVIEW);
      },
      deployText() {
        return this.getMessageText(this.messages.DEPLOY);
      },
      archiveText() {
        return this.getMessageText(this.messages.ARCHIVE);
      },
      cancelText() {
        return this.getMessageText(this.messages.CANCEL);
      },
      okText() {
        return this.getMessageText(this.messages.OK);
      },
      pagesPath() {
        return `#/apps/${this.app.uuid}/pages?preview=true`;
      },
      routeParams() {
        return {
          appId: this.app.uuid,
          appName: this.app.name,
          appTestRecord: this.app.testRecord
        };
      }
    },
    methods: {
      downloadTemplate() {
        const component = this;
        let showLoader = true;

        setTimeout(() => {
          if (showLoader) {
            store.commit(MUTATION_NAME.SET_LATEST_APPS, { fetch: true });
          }
        }, 500);

        templateServices
          .downloadFile(this.app.uuid)
          .then(
            resp => {
              if (!resp.headers["content-disposition"]) {
                component.$bvToast.toast(
                  component.getMessageText(
                    component.messages.ERROR_WHILE_LOADING
                  ),
                  TOAST_CONFIG.BOTTOM_CENTER_ERROR
                );
                return;
              }
              const fileURL = window.URL.createObjectURL(
                new Blob([resp.data], { type: "application/zip" })
              );

              let link = document.createElement("a");
              link.href = fileURL;
              link.target = "_self";
              const encodedName = resp.headers["content-disposition"].replace(
                "attachment;filename=",
                ""
              );
              link.download = decodeURIComponent(encodedName);
              link.click();
              window.URL.revokeObjectURL(fileURL);
            },
            () => {
              component.$bvToast.toast(
                component.getMessageText(
                  component.messages.ERROR_WHILE_LOADING
                ),
                TOAST_CONFIG.BOTTOM_CENTER_ERROR
              );
            }
          )
          .finally(() => {
            showLoader = false;
            store.commit(MUTATION_NAME.SET_LATEST_APPS, null);
          });
      },
      showConfirmModal() {
        return this.$bvModal.msgBoxConfirm(
          this.getMessageText(this.messages.ARE_YOU_SURE),
          {
            size: "sm",
            buttonSize: "sm",
            okVariant: "danger",
            okTitle: this.okText,
            cancelTitle: this.cancelText,
            footerClass: "p-2",
            hideHeaderClose: false,
            centered: true
          }
        );
      },
      actionErrorHandler() {
        this.$bvToast.toast(
          this.getMessageText(this.messages.SAVE_ERROR),
          TOAST_CONFIG.BOTTOM_CENTER_ERROR
        );
      },
      convertToTemplateConfirm() {
        this.showConfirmModal()
          .then(value => {
            if (value) {
              let params = {
                name: this.app.name,
                description: this.app.description,
                appId: this.app.uuid,
                record: this.app.testRecord,
                isTemplate: true,
                sourceTemplateUuid: this.app.sourceTemplateUuid,
                sourceTemplateVersion: this.app.sourceTemplateVersion
              };

              services
                .updateAppSetting(params)
                .then(() => {
                  this.$router.go();
                }, this.actionErrorHandler)
                .catch(this.actionErrorHandler);
            }
          })
          .catch(console.error);
      },
      updateSourceTemplateConfirm() {
        this.showConfirmModal()
          .then(value => {
            if (value) {
              let params = {
                name: this.app.name,
                description: this.app.description,
                appId: this.app.uuid,
                record: this.app.testRecord
              };

              let sourceTemplate = utils.findAppByAppId(
                this.apps,
                this.app.sourceTemplateUuid
              );
              templateService
                .updateWithFile(this.app.sourceTemplateUuid, null, params)
                .then(resp => {
                  if (!resp) {
                    this.actionErrorHandler();
                    return;
                  }

                  const app = JSON.parse(JSON.stringify(sourceTemplate));
                  app.version = resp.version;
                  store.commit(MUTATION_NAME.UPDATE_APP, app);
                  this.$bvToast.toast(
                    this.getMessageText(this.messages.SAVE_SUCCESS),
                    TOAST_CONFIG.BOTTOM_CENTER_SUCCESS
                  );
                }, this.actionErrorHandler)
                .catch(this.actionErrorHandler);
            }
          })
          .catch(console.error);
      },
      goToRoute(routeName) {
        const route = {
          name: routeName,
          params: this.routeParams
        };
        if (routeName === "Preview") {
          route.query = {
            preview: true
          };
        }
        this.$router.push(route);
      }
    }
  };
</script>

<style scoped></style>
